/**
 * Rendering for phase 2 §3.2's relationship rows.
 *
 * An organization can hold several at once — a partner agency that subcontracts
 * to Matchbox and also buys from it — so the badges are a list rather than a
 * single label, and an ended one keeps its place with its dates. §3.3 is blunt
 * about why the closed rows matter: a client who leaves and returns is two
 * engagements, and collapsing them to a current state loses the gap.
 */

import type { OrganizationRelationship, OrgRelationshipType } from '../api/types.ts';
import { Badge, type BadgeTone } from './ui/badge.tsx';

const TONES: Record<OrgRelationshipType, BadgeTone> = {
  client: 'brand',
  prospect: 'warning',
  vendor: 'outline',
  partner: 'outline',
  internal: 'gray',
};

export const RELATIONSHIP_LABELS: Record<OrgRelationshipType, string> = {
  client: 'Client',
  prospect: 'Prospect',
  vendor: 'Vendor',
  partner: 'Partner',
  internal: 'Internal',
};

/** The four a person may choose. `internal` is a singleton and belongs to the bootstrap (§3.5). */
export const RELATIONSHIP_OPTIONS = [
  {
    value: 'prospect' as const,
    label: RELATIONSHIP_LABELS.prospect,
    description: 'A pipeline record. No space, no ACL, no place to put documents.',
  },
  {
    value: 'client' as const,
    label: RELATIONSHIP_LABELS.client,
    description:
      'Creates the space pair in the same transaction — the internal space and its portal home.',
  },
  {
    value: 'vendor' as const,
    label: RELATIONSHIP_LABELS.vendor,
    description: 'Somebody Matchbox buys from. A record only.',
  },
  {
    value: 'partner' as const,
    label: RELATIONSHIP_LABELS.partner,
    description: 'An agency Matchbox works alongside. A record only.',
  },
];

export function RelationshipBadges({
  relationships,
}: {
  relationships: OrganizationRelationship[];
}) {
  const open = relationships.filter((r) => r.endedOn === null);
  const ended = relationships.filter((r) => r.endedOn !== null);

  if (open.length === 0 && ended.length === 0) {
    return <span className="text-sm text-tertiary">—</span>;
  }

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {open.map((relationship) => (
        <Badge key={relationship.id} tone={TONES[relationship.type]}>
          {RELATIONSHIP_LABELS[relationship.type]}
        </Badge>
      ))}
      {/* Ended ones are dimmed rather than dropped: "was a client until March"
          is the fact somebody is looking for when they open this record. */}
      {ended.map((relationship) => (
        <Badge key={relationship.id} tone="gray">
          {RELATIONSHIP_LABELS[relationship.type]} · ended
        </Badge>
      ))}
    </span>
  );
}
